const MONEY_FIELDS = `#graphql
  fragment MoneyFields on MoneyV2 {
    amount
    currencyCode
  }
`;

/** 금액만 필요한 곳 (기존 moneyFragment) */
const MONEY_AMOUNT_FIELDS = `#graphql
  fragment MoneyAmountFields on MoneyV2 {
    amount
  }
`;

const IMAGE_FIELDS = `#graphql
  fragment ImageFields on Image {
    altText
    height
    id
    url
    width
  }
`;

const METAFIELD_FIELDS = `#graphql
  fragment MetafieldFields on Metafield {
    key
    type
    value
    namespace
  }
`;

const PAGE_INFO_FIELDS = `#graphql
  fragment PageInfoFields on PageInfo {
    hasNextPage
    hasPreviousPage
    endCursor
    startCursor
  }
`;

const SELECTED_OPTION_FIELDS = `#graphql
  fragment SelectedOptionFields on SelectedOption {
    name
    value
  }
`;

const SEO_FIELDS = `#graphql
  fragment SeoFields on SEO {
    description
    title
  }
`;

const ATTRIBUTE_FIELDS = `#graphql
  fragment AttributeFields on Attribute {
    key
    value
  }
`;

const FILTER_FIELDS = `#graphql
  fragment FilterFields on Filter {
    id
    label
    type
    values {
      id
      label
      input
      count
    }
  }
`;

const MAILING_ADDRESS_FIELDS = `#graphql
  fragment MailingAddressFields on MailingAddress {
    address1
    address2
    city
    company
    country
    countryCodeV2
    firstName
    lastName
    phone
    province
    provinceCode
    zip
    id
  }
`;

const CUSTOMER_BASE_FIELDS = `#graphql
  fragment CustomerBaseFields on Customer {
    id
    firstName
    lastName
    acceptsMarketing
    email
    phone
    tags
  }
`;

const CUSTOMER_ACCESS_TOKEN_FIELDS = `#graphql
  fragment CustomerAccessTokenFields on CustomerAccessToken {
    accessToken
    expiresAt
  }
`;

const CUSTOMER_USER_ERROR_FIELDS = `#graphql
  fragment CustomerUserErrorFields on CustomerUserError {
    code
    field
    message
  }
`;

const USER_ERROR_FIELDS = `#graphql
  fragment UserErrorFields on UserError {
    field
    message
  }
`;

const CART_USER_ERROR_FIELDS = `#graphql
  fragment CartUserErrorFields on CartUserError {
    code
    field
    message
  }
`;

const ORDER_BASE_FIELDS = `#graphql
  fragment OrderBaseFields on Order {
    id
    name
    orderNumber
    processedAt
    canceledAt
    phone
    financialStatus
    fulfillmentStatus
    cancelReason
    currentTotalPrice {
      ...MoneyFields
    }
    totalTax {
      ...MoneyFields
    }
    totalPrice {
      ...MoneyFields
    }
    subtotalPrice {
      ...MoneyFields
    }
    totalShippingPrice {
      ...MoneyFields
    }
    shippingAddress {
      ...MailingAddressFields
    }
    billingAddress {
      ...MailingAddressFields
    }
    totalRefunded {
      ...MoneyFields
    }
    customAttributes {
      ...AttributeFields
    }
  }
`;

/** 주문 금액 합산 전용 (기존 orderAmountFields) */
const ORDER_AMOUNT_FIELDS = `#graphql
  fragment OrderAmountFields on Order {
    name
    processedAt
    canceledAt
    financialStatus
    fulfillmentStatus
    currentTotalPrice {
      ...MoneyFields
    }
    totalPrice {
      ...MoneyFields
    }
    subtotalPrice {
      ...MoneyFields
    }
    totalRefunded {
      ...MoneyFields
    }
  }
`;

const ARTICLE_BASE_FIELDS = `#graphql
  fragment ArticleBaseFields on Article {
    authorV2 {
      name
    }
    contentHtml
    content
    excerptHtml
    handle
    id
    image {
      ...ImageFields
    }
    publishedAt
    title
    tags
    seo {
      ...SeoFields
    }
    blog {
      handle
    }
  }
`;

const PRODUCT_OPTION_FIELDS = `#graphql
  fragment ProductOptionFields on ProductOption {
    id
    name
    values
  }
`;

const PRODUCT_PRICE_RANGE_FIELDS = `#graphql
  fragment ProductPriceRangeFields on ProductPriceRange {
    maxVariantPrice {
      ...MoneyFields
    }
    minVariantPrice {
      ...MoneyFields
    }
  }
`;

/** 상품(product) 중첩 없이 variant 자체 필드만 (기존 variantFields) */
const VARIANT_BASE_FIELDS = `#graphql
  fragment VariantBaseFields on ProductVariant {
    availableForSale
    barcode
    compareAtPrice {
      ...MoneyFields
    }
    currentlyNotInStock
    id
    image {
      ...ImageFields
    }
    price {
      ...MoneyFields
    }
    quantityAvailable
    selectedOptions {
      ...SelectedOptionFields
    }
    sku
    weight
    weightUnit
    title
  }
`;

const MEDIA_IMAGE_FIELDS = `#graphql
  fragment MediaImageFields on MediaImage {
    __typename
    id
    mediaContentType
    image {
      ...ImageFields
    }
  }
`;

const VIDEO_FIELDS = `#graphql
  fragment VideoFields on Video {
    __typename
    id
    alt
    mediaContentType
    sources {
      url
      format
    }
  }
`;

/** 미리보기 이미지까지 필요한 비디오 (기존 videoImageFragment) */
const VIDEO_IMAGE_FIELDS = `#graphql
  fragment VideoImageFields on Video {
    id
    sources {
      url
      width
      height
      format
      mimeType
    }
    previewImage {
      ...ImageFields
    }
  }
`;

const METAOBJECT_KEY_VALUE_FIELDS = `#graphql
  fragment MetaobjectKeyValueFields on Metaobject {
    id
    fields {
      key
      value
      type
    }
  }
`;

const METAOBJECT_IMAGE_FIELDS = `#graphql
  fragment MetaobjectImageFields on Metaobject {
    id
    fields {
      key
      value
      type
      reference {
        ...MediaImageFields
      }
    }
  }
`;

const CART_CODE_DISCOUNT_ALLOCATION_FIELDS = `#graphql
  fragment CartCodeDiscountAllocationFields on CartCodeDiscountAllocation {
    __typename
    code
    discountedAmount {
      ...MoneyFields
    }
  }
`;

const CART_CUSTOM_DISCOUNT_ALLOCATION_FIELDS = `#graphql
  fragment CartCustomDiscountAllocationFields on CartCustomDiscountAllocation {
    __typename
    title
    discountedAmount {
      ...MoneyFields
    }
  }
`;

const CART_AUTOMATIC_DISCOUNT_ALLOCATION_FIELDS = `#graphql
  fragment CartAutomaticDiscountAllocationFields on CartAutomaticDiscountAllocation {
    __typename
    title
    discountedAmount {
      ...MoneyFields
    }
  }
`;

const MANUAL_DISCOUNT_APPLICATION_FIELDS = `#graphql
  fragment ManualDiscountApplicationFields on ManualDiscountApplication {
    __typename
    title
    description
  }
`;

const AUTOMATIC_DISCOUNT_APPLICATION_FIELDS = `#graphql
  fragment AutomaticDiscountApplicationFields on AutomaticDiscountApplication {
    __typename
    title
  }
`;

const DISCOUNT_CODE_APPLICATION_FIELDS = `#graphql
  fragment DiscountCodeApplicationFields on DiscountCodeApplication {
    __typename
    code
    applicable
  }
`;

const PRICING_PERCENTAGE_FIELDS = `#graphql
  fragment PricingPercentageFields on PricingPercentageValue {
    percentage
  }
`;

/** 모든 스토어가 공통으로 쓰는 Product 필드. 스토어별 fragment에서 spread 해서 사용한다. */
const PRODUCT_COMMON_FIELDS = `#graphql
  fragment ProductCommonFields on Product {
    availableForSale
    compareAtPriceRange {
      ...ProductPriceRangeFields
    }
    createdAt
    description
    descriptionHtml
    featuredImage {
      ...ImageFields
    }
    handle
    id
    options {
      ...ProductOptionFields
    }
    priceRange {
      ...ProductPriceRangeFields
    }
    productType
    publishedAt
    tags
    title
    totalInventory
    vendor
  }
`;

const PRODUCT_REF_FIELDS = `#graphql
  fragment ProductRefFields on Product {
    handle
    id
    productType
    tags
    title
    vendor
  }
`;

const PRODUCT_FIELDS_TUNE = `#graphql
  fragment ProductFieldsTune on Product {
    quantityLimitProduct: metafield(namespace: "custom", key: "quantity_limit") {
      ...MetafieldFields
    }
    draw: metafield(namespace: "custom", key: "draw") {
      ...MetafieldFields
      reference {
        ...MetaobjectKeyValueFields
      }
    }
    ...ProductCommonFields
  }
`;

const PRODUCT_FIELDS_WELLBEINGEXPRESS = `#graphql
  fragment ProductFieldsWellbeingexpress on Product {
    images(first: 50) {
      edges {
        node {
          ...ImageFields
        }
      }
    }
    ...ProductCommonFields
  }
`;

const MENU_ITEM_BASE_FIELDS = `#graphql
  fragment MenuItemBaseFields on MenuItem {
    id
    title
    url
  }
`;

const SHOP_POLICY_FIELDS = `#graphql
  fragment ShopPolicyFields on ShopPolicy {
    body
    handle
    id
    title
    url
  }
`;

/** 이 파일의 모든 fragment (registry 등록용) */
export const COMMON_FRAGMENTS = [
  MONEY_FIELDS,
  MONEY_AMOUNT_FIELDS,
  IMAGE_FIELDS,
  METAFIELD_FIELDS,
  PAGE_INFO_FIELDS,
  SELECTED_OPTION_FIELDS,
  SEO_FIELDS,
  ATTRIBUTE_FIELDS,
  FILTER_FIELDS,
  MAILING_ADDRESS_FIELDS,
  CUSTOMER_BASE_FIELDS,
  CUSTOMER_ACCESS_TOKEN_FIELDS,
  CUSTOMER_USER_ERROR_FIELDS,
  USER_ERROR_FIELDS,
  CART_USER_ERROR_FIELDS,
  ORDER_BASE_FIELDS,
  ORDER_AMOUNT_FIELDS,
  ARTICLE_BASE_FIELDS,
  PRODUCT_OPTION_FIELDS,
  PRODUCT_PRICE_RANGE_FIELDS,
  VARIANT_BASE_FIELDS,
  MEDIA_IMAGE_FIELDS,
  VIDEO_FIELDS,
  VIDEO_IMAGE_FIELDS,
  METAOBJECT_KEY_VALUE_FIELDS,
  METAOBJECT_IMAGE_FIELDS,
  CART_CODE_DISCOUNT_ALLOCATION_FIELDS,
  CART_CUSTOM_DISCOUNT_ALLOCATION_FIELDS,
  CART_AUTOMATIC_DISCOUNT_ALLOCATION_FIELDS,
  MANUAL_DISCOUNT_APPLICATION_FIELDS,
  AUTOMATIC_DISCOUNT_APPLICATION_FIELDS,
  DISCOUNT_CODE_APPLICATION_FIELDS,
  PRICING_PERCENTAGE_FIELDS,
  PRODUCT_COMMON_FIELDS,
  PRODUCT_REF_FIELDS,
  PRODUCT_FIELDS_TUNE,
  PRODUCT_FIELDS_WELLBEINGEXPRESS,
  MENU_ITEM_BASE_FIELDS,
  SHOP_POLICY_FIELDS,
];
